import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

// Typing effect for the tagline
const TypedText = ({ text }: { text: string }) => {
  const [displayText, setDisplayText] = React.useState('');
  const [currentIndex, setCurrentIndex] = React.useState(0);
  
  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 100);
      
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);
  
  return (
    <span>{displayText}<span className="animate-pulse">|</span></span>
  );
};

const HeroSection = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      
      const scrollY = window.scrollY;
      const heroHeight = heroRef.current.offsetHeight;
      const opacity = 1 - (scrollY / heroHeight) * 1.5;
      
      if (heroRef.current) {
        heroRef.current.style.opacity = Math.max(0, Math.min(1, opacity)).toString();
        heroRef.current.style.transform = `translateY(${scrollY * 0.4}px)`;
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <div className="relative overflow-hidden bg-tech-dark text-white" id="hero">
      {/* Background with subtle moving gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-accent/10 via-tech-dark to-tech-dark"></div>
      
      {/* Animated grid lines for tech effect */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,_#8b5cf620_1px,_transparent_1px),_linear-gradient(to_bottom,_#8b5cf620_1px,_transparent_1px)] bg-[size:14px_24px]"></div>
      
      <div className="container mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-center min-h-[90vh] py-16 container-padding" ref={heroRef}>
          <div className="flex-1 space-y-6 text-center lg:text-left lg:pr-12">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight">
              <span className="gradient-text">Custom Gadget Solutions</span> <br />
              Engineered For Excellence
            </h1>
            
            <h2 className="text-xl md:text-2xl font-medium text-gray-300 min-h-[80px]">
              <TypedText text="25 Years of Building Tech That Works For You" />
            </h2>
            
            <p className="text-lg text-gray-400 max-w-2xl mx-auto lg:mx-0">
              Truecore Gadget Innovations creates tailor-made technology solutions designed to meet your exact requirements. From rugged tablets to high-performance PCs, we build hardware that empowers your vision.
            </p>
            
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center lg:justify-start pt-4">
              <a href="#products" className="tech-btn-primary">
                Explore Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a 
                href="#contact" 
                className="tech-btn-outline hover:bg-primary/20 transition-all duration-300 group flex items-center justify-center border-primary text-primary"
              >
                Get Free Quote
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
            
            <div className="hidden md:flex items-center space-x-8 pt-6 justify-center lg:justify-start">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <span className="text-gray-300">2000+ Clients</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <span className="text-gray-300">5-Year Warranty</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <span className="text-gray-300">24/7 Support</span>
              </div>
            </div>
          </div>
          
          <div className="flex-1 mt-12 lg:mt-0 relative">
            <div className="relative z-10 animate-float">
              <div className="relative w-full max-w-lg mx-auto">
                {/* The main product image */}
                <div className="glow">
                  <img 
                    src="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=800&auto=format&fit=crop" 
                    alt="Custom Technology" 
                    className="rounded-lg shadow-2xl w-full"
                  />
                </div>
                
                {/* Floating UI elements for tech effect */}
                <div className="absolute -right-10 -bottom-10 bg-tech-dark/80 backdrop-blur border border-accent/30 rounded-lg p-3 shadow-lg animate-pulse-slow">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-accent rounded-full"></div>
                    <span className="text-sm text-gray-300">Custom Hardware</span>
                  </div>
                </div>
                
                <div className="absolute -left-8 top-1/4 bg-tech-dark/80 backdrop-blur border border-primary/30 rounded-lg p-3 shadow-lg transform -translate-y-1/2 animate-pulse-slow" style={{ animationDelay: '0.5s' }}>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-primary rounded-full"></div>
                    <span className="text-sm text-gray-300">Tailored Solutions</span>
                  </div>
                </div>
                
                <div className="absolute left-1/4 -top-8 bg-tech-dark/80 backdrop-blur border border-secondary/30 rounded-lg p-3 shadow-lg animate-pulse-slow" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-secondary rounded-full"></div>
                    <span className="text-sm text-gray-300">25+ Years Experience</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Background decorative elements */}
            <div className="absolute -inset-4 bg-glow-gradient opacity-40 blur-3xl -z-10"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/10 rounded-full blur-3xl -z-10"></div>
            <div className="absolute top-3/4 left-1/4 w-40 h-40 bg-secondary/10 rounded-full blur-3xl -z-10"></div>
          </div>
        </div>
      </div>
      
      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
          <path fill="currentColor" fillOpacity="1" d="M0,64L80,80C160,96,320,128,480,128C640,128,800,96,960,80C1120,64,1280,64,1360,64L1440,64L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"></path>
        </svg>
      </div>
    </div>
  );
};

export default HeroSection;
